import { useLayoutEffect, useState } from 'react';

import { placeComments } from './anchoring';

export const NO_PLACED_RANGES = new Map();
const NOTHING_PLACED = { comments: NO_PLACED_RANGES, draft: null };

// How long the report's DOM has to stay still before comments are placed
// again.
const PLACE_AGAIN_AFTER = 200;

const sameRange = (a, b) =>
    a === b ||
    (!!a &&
        !!b &&
        a.startContainer === b.startContainer &&
        a.startOffset === b.startOffset &&
        a.endContainer === b.endContainer &&
        a.endOffset === b.endOffset);

// Placing again only re-renders when something moved. Charts and Fenix
// blocks change their DOM when they render, which would otherwise place and
// render the report again and again.
const samePlacements = (a, b) =>
    sameRange(a.draft, b.draft) &&
    a.comments.size === b.comments.size &&
    Array.from(a.comments).every(([id, range]) =>
        sameRange(range, b.comments.get(id))
    );

// Where each comment's selected text is in the report, by comment id, and
// where the draft's is. Comments missing from it are orphaned.
//
// Placed before the browser paints, so a comment that has just been posted
// isn't shown as orphaned first. Placed again whenever the report's DOM
// changes, since a range stops matching the text once its text node is
// replaced, as rich text is after mount and blocks are when they load.
const usePlacements = (commentableRef, comments, draftSelection) => {
    const [placements, setPlacements] = useState(NOTHING_PLACED);

    useLayoutEffect(() => {
        const root = commentableRef.current;
        if (!root || !comments) {
            setPlacements(NOTHING_PLACED);
            return;
        }

        const place = () => {
            const anchors = draftSelection
                ? [draftSelection, ...comments]
                : comments;
            const ranges = placeComments(root, anchors);
            const draft = draftSelection ? ranges.shift() : null;
            const placed = {
                comments: new Map(
                    comments.flatMap((comment, index) =>
                        ranges[index] ? [[comment.id, ranges[index]]] : []
                    )
                ),
                draft,
            };
            setPlacements((current) =>
                samePlacements(current, placed) ? current : placed
            );
        };

        place();
        let timeout = null;
        const observer = new MutationObserver(() => {
            clearTimeout(timeout);
            timeout = setTimeout(place, PLACE_AGAIN_AFTER);
        });
        observer.observe(root, {
            childList: true,
            characterData: true,
            subtree: true,
        });
        return () => {
            observer.disconnect();
            clearTimeout(timeout);
        };
    }, [commentableRef, comments, draftSelection]);

    return placements;
};

export default usePlacements;
